'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, Loader2, Check, AlertCircle, Lightbulb, Sparkles, ArrowRight, Target, Search, Edit, ArrowDown, RefreshCw, Download } from 'lucide-react';
import { ResultCard, Badge, SuggestionItem, BeforeAfterCard, TipCard } from '@/components/analysis/ResultCard';
// @ts-ignore - No types available for mammoth
import * as mammoth from 'mammoth';
import { extractTextFromPdf } from '@/lib/pdfUtils';

type Verdict = 'APPLY' | 'MAYBE' | 'SKIP' | null;

interface Suggestion {
  new_bullets: string[];
  rewrite_bullets: Array<{ original: string; improved: string }>;
  add_keywords: string[];
}

interface AnalysisResult {
  score: number;
  verdict: Verdict;
  matched: string[];
  missing: string[];
  weak_areas: string[];
  suggestions: Suggestion;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_TEXT_LENGTH = 50000; // 50K characters limit for extracted text
const MAX_JOB_DESC_LENGTH = 10000; // 10K characters limit for job description
const API_ENDPOINT = 'https://resume-ai-ats.onrender.com/analyze';
const REQUEST_TIMEOUT = 30000; // 30 seconds

const VALID_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
];

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function AnalyzePage() {
  const [file, setFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  }, []);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFile(selectedFile);
  };

  // Enhanced file validation
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!VALID_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Unsupported file type: ${file.type}. Please upload a PDF or DOCX file.`
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 2MB limit.`
      };
    }

    return { valid: true };
  };

  // Process the selected file
  const handleFile = async (selectedFile: File) => {
    setError(null);

    // Validate file
    const { valid, error: fileError } = validateFile(selectedFile);
    if (!valid) {
      setError(fileError || 'Invalid file');
      return;
    }

    try {
      setIsLoading(true);
      let text = '';

      if (selectedFile.type === 'application/pdf') {
        text = await extractTextFromPdf(selectedFile);
        if (!text) throw new Error('No text could be extracted from the PDF');
      }
      else if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        text = result.value;
      }

      text = text.replace(/\s+/g, ' ').trim();
      if (!text) throw new Error('The file appears to be empty or contains no extractable text.');

      if (text.length > MAX_TEXT_LENGTH) {
        text = text.substring(0, MAX_TEXT_LENGTH) + '... [truncated]';
      }

      setExtractedText(text);
      setFile(selectedFile);

    } catch (error) {
      const err = error as Error;
      setError(`Failed to process the file: ${err.message || 'Unknown error'}.`);
      setFile(null);
      setExtractedText('');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove the selected file
  const removeFile = () => {
    setFile(null);
    setExtractedText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enhanced fetch with retry and timeout
  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    retries = 2,
    timeout = REQUEST_TIMEOUT
  ): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    options.signal = controller.signal;

    try {
      const response = await fetch(url, options);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      if (retries > 0 && error instanceof Error && error.name !== 'AbortError') {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1000 * (3 - retries)));
        return fetchWithRetry(url, options, retries - 1, timeout);
      }

      throw error;
    }
  };

  // Validate API response
  const validateApiResponse = (data: any): AnalysisResult => {
    if (typeof data !== 'object' || data === null) {
      throw new Error('Invalid API response format');
    }

    return {
      score: typeof data.score === 'number' ? Math.min(100, Math.max(0, data.score)) : 0,
      verdict: ['APPLY', 'MAYBE', 'SKIP'].includes(data.verdict) ? data.verdict : null,
      matched: Array.isArray(data.matched) ? data.matched : [],
      missing: Array.isArray(data.missing) ? data.missing : [],
      weak_areas: Array.isArray(data.weak_areas) ? data.weak_areas : [],
      suggestions: {
        new_bullets: Array.isArray(data.suggestions?.new_bullets)
          ? data.suggestions.new_bullets
          : [],
        rewrite_bullets: Array.isArray(data.suggestions?.rewrite_bullets)
          ? data.suggestions.rewrite_bullets.filter((item: any) =>
            typeof item === 'object' &&
            item !== null &&
            'original' in item &&
            'improved' in item
          )
          : [],
        add_keywords: Array.isArray(data.suggestions?.add_keywords)
          ? data.suggestions.add_keywords
          : []
      }
    };
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!file) {
      setError('Please upload a resume file.');
      return;
    }

    if (!jobDescription.trim()) {
      setError('Please enter a job description.');
      return;
    }

    const wordCount = jobDescription.trim().split(/\s+/).length;
    if (wordCount < 50) {
      setError('Job description should be at least 50 words.');
      return;
    }

    if (jobDescription.length > MAX_JOB_DESC_LENGTH) {
      setError(`Job description is too long. Maximum ${MAX_JOB_DESC_LENGTH} characters allowed.`);
      return;
    }

    try {
      setIsLoading(true);

      // Call the API with retry and timeout
      const response = await fetchWithRetry(
        API_ENDPOINT,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resume: extractedText,
            job_description: jobDescription
          })
        }
      );

      const data = await response.json();
      const formattedResult = validateApiResponse(data);

      setResult(formattedResult);

      // Scroll to results
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      // Log successful analysis
      console.log('Analysis completed:', {
        score: formattedResult.score,
        verdict: formattedResult.verdict,
        matchedCount: formattedResult.matched.length,
        missingCount: formattedResult.missing.length
      });

    } catch (error: unknown) {
      console.error('Analysis error:', error);

      let errorMessage = 'Failed to analyze resume. Please try again.';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate word and character counts
  const wordCount = jobDescription.trim() ? jobDescription.trim().split(/\s+/).length : 0;
  const charCount = jobDescription.length;
  const isWordCountValid = wordCount >= 50 && wordCount <= 5000;
  const isJobDescTooLong = charCount > MAX_JOB_DESC_LENGTH;

  // Get color based on verdict
  const getVerdictColor = (verdict: Verdict) => {
    switch (verdict) {
      case 'APPLY': return 'bg-green-100 text-green-800';
      case 'MAYBE': return 'bg-yellow-100 text-yellow-800';
      case 'SKIP': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get score color based on percentage
  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Analyze Your Resume</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Upload your resume and job description to get instant feedback on how to optimize for ATS
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* File Upload Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-sky-50 mr-2">
                    <Upload className="h-5 w-5 text-sky-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Upload Resume</h2>
                </div>

                <div className="mt-4">
                  <div
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const droppedFile = e.dataTransfer.files[0];
                      if (droppedFile) handleFile(droppedFile);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onClick={() => fileInputRef.current?.click()}
                    className={`mt-1 flex justify-center px-4 pt-6 pb-5 border-2 border-dashed rounded-lg ${isDragging
                      ? 'border-sky-500 bg-sky-50'
                      : file
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300 hover:border-sky-400 hover:bg-gray-50'
                      } transition-colors cursor-pointer`}
                  >
                    <div className="space-y-3 text-center">
                      {isLoading ? (
                        <Loader2 className="mx-auto h-10 w-10 text-sky-500 animate-spin" />
                      ) : file ? (
                        <div className="flex flex-col items-center">
                          <Check className="h-10 w-10 text-green-500 mb-2" />
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile();
                            }}
                            className="mt-2 text-sm text-sky-600 hover:text-sky-800 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload className={`mx-auto h-10 w-10 ${isDragging ? 'text-sky-500' : 'text-gray-400'}`} />
                          <div className="text-sm text-gray-600 text-center">
                            <p className="font-medium">
                              {isDragging ? 'Drop your resume here' : 'Drag and drop your resume here'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">or click to browse files</p>
                          </div>
                          <p className="text-xs text-gray-500">PDF or DOCX (max. 2MB)</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx"
                      className="hidden"
                      onChange={handleFileSelect}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Job Description Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-blue-50 mr-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Job Description</h2>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="job-description" className="block text-sm font-medium text-gray-700">
                      Paste the job description you're applying for
                    </label>
                    <span className={`text-xs ${isWordCountValid ? 'text-green-600' : 'text-red-600'}`}>
                      {wordCount} {wordCount === 1 ? 'word' : 'words'}
                      {!isWordCountValid && wordCount > 0 && (
                        <span className="ml-1 text-amber-600">
                          ({wordCount < 50 ? 'min 50 required' : 'max 5000 allowed'})
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="mt-1">
                    <div className="relative">
                      <textarea
                        id="jobDescription"
                        rows={4}
                        className={`block w-full text-gray-900 rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm resize-y ${isJobDescTooLong ? 'border-red-300' : ''
                          }`}
                        placeholder="Paste the job description here (minimum 50 words)..."
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                      />
                      <div className="mt-1 flex justify-between text-xs text-gray-500">
                        <span className={isJobDescTooLong ? 'text-red-600' : ''}>
                          {wordCount} {wordCount === 1 ? 'word' : 'words'}
                          {!isWordCountValid && wordCount > 0 && (
                            <span className="ml-1 text-amber-600">
                              ({wordCount < 50 ? 'min 50 required' : 'max 5000 allowed'})
                            </span>
                          )}
                        </span>
                        <span className={charCount > MAX_JOB_DESC_LENGTH ? 'text-red-600' : ''}>
                          {charCount}/{MAX_JOB_DESC_LENGTH} characters
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    The more detailed the job description, the better our analysis will be.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="rounded-md bg-red-50 p-4 border border-red-100"
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !file || !jobDescription.trim() || !isWordCountValid}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${isLoading || !file || !jobDescription.trim() || !isWordCountValid
                ? 'bg-sky-300 cursor-not-allowed'
                : 'bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500'
                }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="-ml-1 mr-2 h-5 w-5" />
                  Analyze Resume
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Results Section */}
        {result && (
          <div ref={resultsRef} className="mt-16 pt-8 border-t-2 border-gray-200">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Score Summary Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="mb-6 md:mb-0">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Your ATS Score: <span className={getScoreColor(result.score)}>{result.score}/100</span>
                      </h2>
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getVerdictColor(result.verdict)}`}>
                          {result.verdict === 'APPLY' ? 'Strong Match' : result.verdict === 'MAYBE' ? 'Moderate Match' : 'Needs Improvement'}
                        </span>
                        <p className="ml-3 text-sm text-gray-600">
                          {result.verdict === 'APPLY'
                            ? 'Your resume looks great! You should apply.'
                            : result.verdict === 'MAYBE'
                              ? 'Your resume could use some improvements.'
                              : 'Your resume needs significant improvements to be competitive.'}
                        </p>
                      </div>
                    </div>

                    <div className="w-full md:w-1/2">
                      <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${result.score >= 75
                            ? 'bg-green-500'
                            : result.score >= 50
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                            }`}
                          style={{ width: `${result.score}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>0%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Matched Skills */}
              {result.matched.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-50 mr-3">
                        <Check className="h-6 w-6 text-green-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">Matched Skills</h2>
                      <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {result.matched.length} found
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4">
                      {result.matched.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {skill}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100">
                      <p className="text-sm text-green-700">
                        <span className="font-medium">Great job!</span> These skills are a strong match for the job description.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Missing Keywords */}
              {result.missing.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-red-50 mr-3">
                        <X className="h-6 w-6 text-red-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">Missing Keywords</h2>
                      <span className="ml-auto bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {result.missing.length} missing
                      </span>
                    </div>

                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-4">
                        These keywords from the job description are missing from your resume. Consider adding them if relevant to your experience.
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {result.missing.map((keyword, index) => (
                          <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                            {keyword}
                          </span>
                        ))}
                      </div>

                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">Tip:</span> Add these keywords naturally into your resume if you have relevant experience. Don't keyword stuff!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Areas for Improvement */}
              {result.weak_areas.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-yellow-50 mr-3">
                        <Lightbulb className="h-6 w-6 text-yellow-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">Areas for Improvement</h2>
                    </div>

                    <div className="space-y-4">
                      {result.weak_areas.map((area, index) => (
                        <div key={index} className="flex items-start">
                          <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-full bg-yellow-100 text-yellow-600 mt-0.5">
                            <AlertCircle className="h-3 w-3" />
                          </div>
                          <p className="ml-3 text-sm text-gray-700">{area}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {result.suggestions && (
                <div className="space-y-6">
                  {/* New Bullets */}
                  {result.suggestions.new_bullets.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <div className="p-2 rounded-lg bg-purple-50 mr-3">
                            <Sparkles className="h-6 w-6 text-purple-600" />
                          </div>
                          <h2 className="text-xl font-semibold text-gray-900">Suggested Bullet Points</h2>
                          <span className="ml-auto bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            {result.suggestions.new_bullets.length} suggestions
                          </span>
                        </div>

                        <div className="space-y-4">
                          {result.suggestions.new_bullets.map((bullet, index) => (
                            <div key={index} className="flex items-start">
                              <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 mt-0.5">
                                <ArrowRight className="h-3 w-3" />
                              </div>
                              <p className="ml-3 text-sm text-gray-700">{bullet}</p>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-sm text-purple-700">
                            <span className="font-medium">Pro Tip:</span> These bullet points are tailored to the job description and can help strengthen your resume.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rewrite Bullets */}
                  {result.suggestions.rewrite_bullets.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <div className="p-2 rounded-lg bg-indigo-50 mr-3">
                            <Edit className="h-6 w-6 text-indigo-600" />
                          </div>
                          <h2 className="text-xl font-semibold text-gray-900">Rewrite Suggestions</h2>
                          <span className="ml-auto bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            {result.suggestions.rewrite_bullets.length} suggestions
                          </span>
                        </div>

                        <div className="space-y-6">
                          {result.suggestions.rewrite_bullets.map((bullet, index) => (
                            <div key={index} className="space-y-2">
                              <div className="text-sm font-medium text-gray-700">Consider rewriting:</div>
                              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm text-gray-600 line-through">
                                {bullet.original}
                              </div>
                              <div className="flex items-center text-green-600">
                                <ArrowDown className="h-4 w-4 mr-2" />
                                <span className="text-xs font-medium">Improved version</span>
                              </div>
                              <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-sm text-gray-800">
                                {bullet.improved}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Next Steps */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Next Steps</h2>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 mr-3">
                        1
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Incorporate the suggestions</h3>
                        <p className="text-sm text-gray-600">Update your resume with the recommended changes to improve your ATS score.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 mr-3">
                        2
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Save your updated resume</h3>
                        <p className="text-sm text-gray-600">Download your optimized resume and use it for your job applications.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 mr-3">
                        3
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Track your applications</h3>
                        <p className="text-sm text-gray-600">Keep track of where you've applied and follow up appropriately.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        setResult(null);
                        setFile(null);
                        setExtractedText('');
                        setJobDescription('');
                        setError(null);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-semibold rounded-lg shadow-lg text-white bg-sky-600 hover:bg-sky-700 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                    >
                      <RefreshCw className="-ml-1 mr-2 h-5 w-5" />
                      Analyze Another Resume
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>

  );
}
